import { useMemo } from 'react'
import Decimal from 'decimal.js'

import { Amount, Position, ClmmPool, SupplyPool, Price } from '@kunalabs-io/kai'
import { PhantomTypeArgument, StructClass, TypeArgument } from '@/shared/gen/_framework/reified'
import { useObjectBcsBatch } from '@/shared/hooks/batshitObjectFetcher'
import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'

import { logErrorMessage } from './sentry-utils'

export interface PositionDisplayInfo {
  position: Position<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>
  isDeleted: boolean
  creationTimestampMs: number
  deletionTimestampMs?: number
  positionCapId: string
  poolName: string
  pool: ClmmPool<StructClass, unknown, PhantomTypeArgument, PhantomTypeArgument>
  supplyPoolX: SupplyPool<PhantomTypeArgument, PhantomTypeArgument>
  supplyPoolY: SupplyPool<PhantomTypeArgument, PhantomTypeArgument>
  range: { start: Decimal; end: Decimal }
  feeRate: bigint
  inRange: boolean | undefined
  lpAmounts: { x: Amount; y: Amount }
  assets: { x: Amount; y: Amount }
  cx: Amount
  cy: Amount
  dx: Amount
  dy: Amount
  equity: {
    x: Decimal
    y: Decimal
  }
  debt: {
    x: Amount
    y: Amount
  }
  interestRates: {
    x: Decimal
    y: Decimal
    effective: Decimal
  }
  marginLevel: Decimal | undefined
  leverage: Decimal | undefined
  liquidationPrices: [Price<string, string>, Price<string, string>] | undefined
  health: Decimal | undefined
  status: 'active' | 'inactive' | 'closed'
  protocolImgSrc?: string
  lpAmountXPercentage: string
  lpAmountYPercentage: string
}

export function calcEffectiveInterestRate(args: {
  dx: Amount
  dy: Amount
  irx: Decimal
  iry: Decimal
  poolPrice: Price<string, string>
}) {
  const dxValue = args.dx.toDecimal().mul(args.poolPrice.human)
  const dyValue = args.dy.toDecimal()

  const interestValue = dxValue.mul(args.irx).plus(dyValue.mul(args.iry))
  const debtValue = dxValue.plus(dyValue)

  if (debtValue.isZero()) {
    return new Decimal(0)
  }

  return interestValue.div(debtValue)
}

export function calcLpAmountsPercentages(args: {
  x: Amount
  y: Amount
  poolPrice: Price<string, string>
}) {
  const xValue = args.x.toDecimal().mul(args.poolPrice.human)
  const yValue = args.y.toDecimal()

  const total = xValue.plus(yValue)

  if (total.isZero()) {
    return {
      lpAmountXPercentage: '0',
      lpAmountYPercentage: '0',
    }
  }

  const xPercentage = xValue.div(total)
  const yPercentage = new Decimal(1).minus(xPercentage)

  return {
    lpAmountXPercentage: `${(xPercentage.toNumber() * 100).toFixed(2)}%`,
    lpAmountYPercentage: `${(yPercentage.toNumber() * 100).toFixed(2)}%`,
  }
}

export function useUserPositionDisplayInfos(positionInfos: PositionInfo[]) {
  const objectBcsQuery = useObjectBcsBatch([
    ...new Set([
      ...positionInfos.map(p => p.lastPosition.configInfo.configId),
      ...positionInfos.map(p => p.lastPosition.configInfo.poolObjectId),
      ...positionInfos.map(p => p.lastPosition.configInfo.supplyPoolXInfo.id),
      ...positionInfos.map(p => p.lastPosition.configInfo.supplyPoolYInfo.id),
    ]),
  ])
  const objectBcsDataMap = useMemo(() => {
    return new Map(objectBcsQuery.filter(bcs => bcs.data).map(bcs => [bcs.data!.id, bcs.data!]))
  }, [objectBcsQuery])

  const isLoading = objectBcsQuery.some(bcs => bcs.isLoading)

  const positionDisplayInfos: PositionDisplayInfo[] = useMemo(() => {
    const ret = []
    for (const positionInfo of positionInfos) {
      const configInfo = positionInfo.lastPosition.configInfo
      const supplyPoolXBcs = objectBcsDataMap.get(configInfo.supplyPoolXInfo.id)?.bcs
      const supplyPoolYBcs = objectBcsDataMap.get(configInfo.supplyPoolYInfo.id)?.bcs
      const poolBcs = objectBcsDataMap.get(configInfo.poolObjectId)?.bcs
      const configBcs = objectBcsDataMap.get(configInfo.configId)?.bcs

      if (!supplyPoolXBcs || !supplyPoolYBcs || !poolBcs || !configBcs) {
        continue
      }
      const supplyPoolX = configInfo.supplyPoolXInfo.fromBcs(supplyPoolXBcs)
      const supplyPoolY = configInfo.supplyPoolYInfo.fromBcs(supplyPoolYBcs)
      if (!supplyPoolX) {
        logErrorMessage(
          `Cannot load SupplyPool object for supply pool id ${configInfo.supplyPoolXInfo.id}`
        )
        continue
      }
      if (!supplyPoolY) {
        logErrorMessage(
          `Cannot load SupplyPool object for supply pool id ${configInfo.supplyPoolYInfo.id}`
        )
        continue
      }

      const pool = configInfo.poolFromBcs(poolBcs)
      const config = configInfo.configFromBcs(configBcs)

      const lastPosition = positionInfo.lastPosition
      const isDeleted = positionInfo.isDeleted

      let inRange: boolean | undefined
      let lpAmounts: ReturnType<typeof Position.prototype.calcLpAmounts>
      let assets: ReturnType<typeof Position.prototype.calcAssetAmounts>
      let marginLevel: Decimal | undefined
      let leverage: Decimal | undefined
      let health: Decimal | undefined
      let dx: Amount
      let dy: Amount
      let equity: ReturnType<typeof Position.prototype.calcEquityAmountsHuman>
      let interestRates:
        | {
            x: Decimal
            y: Decimal
            effective: Decimal
          }
        | undefined
      let effectiveInterestRate: Decimal | undefined
      let cx: Amount
      let cy: Amount
      let liquidationPrices: [Price<string, string>, Price<string, string>] | undefined
      let status: 'active' | 'inactive' | 'closed'
      if (isDeleted) {
        inRange = undefined
        lpAmounts = {
          x: Amount.fromInt(0n, lastPosition.X.decimals),
          y: Amount.fromInt(0n, lastPosition.Y.decimals),
        }
        assets = {
          x: Amount.fromInt(0n, lastPosition.X.decimals),
          y: Amount.fromInt(0n, lastPosition.Y.decimals),
        }
        marginLevel = undefined
        leverage = undefined
        health = undefined
        dx = Amount.fromInt(0n, lastPosition.X.decimals)
        dy = Amount.fromInt(0n, lastPosition.Y.decimals)
        equity = {
          x: new Decimal(0),
          y: new Decimal(0),
        }
        cx = Amount.fromInt(0n, lastPosition.X.decimals)
        cy = Amount.fromInt(0n, lastPosition.Y.decimals)
        interestRates = {
          x: new Decimal(0),
          y: new Decimal(0),
          effective: new Decimal(0),
        }
        effectiveInterestRate = undefined
        liquidationPrices = undefined
        status = 'closed'
      } else {
        const position = lastPosition

        lpAmounts = position.calcLpAmounts(pool.currentPrice())
        assets = position.calcAssetAmounts(pool.currentPrice())

        marginLevel = position.calcMarginLevel({
          currentPrice: pool.currentPrice(),
          supplyPoolX,
          supplyPoolY,
          timestampMs: Date.now(),
        })
        leverage = marginLevel.div(marginLevel.sub(1))
        health = config.calcHealth(marginLevel)

        const debtAmounts = position.calcDebtAmounts({
          supplyPoolX,
          supplyPoolY,
          timestampMs: Date.now(),
        })
        dx = debtAmounts.x
        dy = debtAmounts.y

        equity = position.calcEquityAmountsHuman({
          poolPrice: pool.currentPrice(),
          supplyPoolX,
          supplyPoolY,
        })

        cx = position.colX
        cy = position.colY

        const ir = position.getInterestRates({
          supplyPoolX,
          supplyPoolY,
          timestampMs: Date.now(),
        })

        effectiveInterestRate = calcEffectiveInterestRate({
          dx,
          dy,
          irx: ir.x,
          iry: ir.y,
          poolPrice: pool.currentPrice(),
        })

        interestRates = {
          x: ir.x,
          y: ir.y,
          effective: effectiveInterestRate,
        }

        liquidationPrices = position.calcLiquidationPrices({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          config: config as any,
          supplyPoolX,
          supplyPoolY,
          timestampMs: Date.now(),
        })

        status = position.isInactive() ? 'inactive' : 'active'
        inRange = status === 'active' ? position.inRange(pool.currentTick()) : undefined
      }

      const { pa, pb } = lastPosition.getRange()

      let protocolImgSrc = undefined
      if (configInfo.isCetus()) {
        protocolImgSrc = '/cetus.png'
      } else if (configInfo.isBluefin()) {
        protocolImgSrc = '/bluefin.png'
      }

      const displayInfo: PositionDisplayInfo = {
        feeRate: pool.feeRate(),
        position: lastPosition,
        isDeleted,
        positionCapId: positionInfo.positionCapId,
        pool,
        supplyPoolX,
        supplyPoolY,
        poolName: configInfo.name,
        range: {
          start: configInfo.isReversedPair ? pa.inverted().human : pa.human,
          end: configInfo.isReversedPair ? pb.inverted().human : pb.human,
        },
        creationTimestampMs: positionInfo.creationTimestampMs,
        deletionTimestampMs: positionInfo.deletionTimestampMs,
        inRange,
        lpAmounts,
        assets,
        debt: { x: dx, y: dy },
        equity,
        interestRates,
        cx,
        cy,
        dx,
        dy,
        marginLevel,
        leverage,
        liquidationPrices,
        health,
        status,
        protocolImgSrc,
        ...calcLpAmountsPercentages({
          x: lpAmounts.x,
          y: lpAmounts.y,
          poolPrice: pool.currentPrice(),
        }),
      }

      ret.push(displayInfo)
    }

    return ret.sort((a, b) => {
      if (a.creationTimestampMs === undefined) return 1
      if (b.creationTimestampMs === undefined) return -1
      return b.creationTimestampMs - a.creationTimestampMs
    })
  }, [positionInfos, objectBcsDataMap])

  return { positionDisplayInfos, isLoading }
}

export function useUserPositionDisplayInfo(positionInfo: PositionInfo) {
  const { positionDisplayInfos, isLoading } = useUserPositionDisplayInfos([positionInfo])
  return { positionDisplayInfo: isLoading ? undefined : positionDisplayInfos[0], isLoading }
}
